import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';

import { IconSolidInfo } from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import { useLivestreams } from '@components/LivestreamBanner/hooks/useLivestreams';
import { ILivestream } from '@components/LivestreamBanner/model';
import { ICharacteristic } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { isEmpty } from '@helpers/utils';
import { ExpandableText } from '@sharedComponents/ExpandableText';
import { trackTab } from '@sharedComponents/TabSwitcher/trackingTab';
import { useIsDesktop } from '@utils/helpers';

import messages from '../../../messages';
import { useProductDetailContext } from '../../../ProductDetailContext';
import { useVirtualTryOnAppearance } from '../../hooks/vto';
import { TabIds } from '../../model';
import { AdditionalText } from '../../ProductBaseInfo/AdditionalText';
import { SkinAnalyser } from '../../ProductBaseInfo/SkinAnalyser';
import { useIsSkinAnalyzerVisible } from '../../ProductBaseInfo/SkinAnalyser/hooks/useIsSkinAnalyzerVisible';
import { VtoLooksCarousel } from '../../VtoLooksCarousel';
import { Perfumers } from '../Perfumers';
import { Pictograms } from '../Pictograms';
import { EPictograms } from '../Pictograms/model';
import { useTabsWithExpandableText } from '../ProductTabContentProvider';
import { TRACKING_ATTRIBUTES } from '../tracking';

import { Characteristics } from './Characteristics';
import { Instructions } from './Instructions';
import { VideoInstructions } from './Instructions/VideoInstructions';
import LivestreamBannersCarousel from './LivestreamBannersCarousel';
import {
  Container,
  MarginWrapper,
  Description,
  SectionTitle,
  HairSensitivityTestLink,
  LivestreamsContainer,
  ExpandButtonText,
  SkinAnalyserWrapper,
  GeneratedTextWrapper,
  VtoLooksContainer,
} from './styled';

export interface IAboutProductProps {
  isForSensitiveHair?: boolean;
  descriptionType?: string;
  description: string;
  pictograms?: EPictograms[];
  characteristics: ICharacteristic[];
  composition: ICharacteristic[];
  isPerfume?: boolean;
  documentation?: Omit<
    NonNullable<
      NonNullable<
        GetCatalogProductViewQuery['productDetailByCatalogMasterId']
      >['variants'][number]['documentation']
    >,
    '__typename'
  > | null;
  perfumers?: Omit<
    NonNullable<
      NonNullable<
        NonNullable<
          GetCatalogProductViewQuery['productDetailByCatalogMasterId']
        >['variants'][number]['features']
      >['perfumers']
    >,
    '__typename'
  > | null;
}

export const AboutProduct: React.FC<IAboutProductProps> = React.memo(
  ({
    description,
    characteristics,
    composition,
    isPerfume,
    documentation,
    pictograms,
    perfumers,
    isForSensitiveHair,
    descriptionType,
  }) => {
    const { formatMessage } = useIntl();
    const { getTimeFromInit } = useTrackingContext();

    const { product } = useProductDetailContext();
    const { livestream_on_pd_enabled } = useFeatureFlags();
    const [livestreamsRef, livestreamsInView] = useInView({
      triggerOnce: true,
      rootMargin: '100px',
    });

    const isDesktop = useIsDesktop();

    const { data, error } = useLivestreams({
      enabled: !!livestream_on_pd_enabled && isDesktop && livestreamsInView,
      productCode: product.masterProductCode,
    });

    const { data: vtoAppearance } = useVirtualTryOnAppearance();

    const shouldRenderVtoLooks =
      isDesktop &&
      (vtoAppearance?.VirtualTryOnAppearance?.products?.length ?? 0) > 0;

    const shouldShowLivestreams = Boolean(
      livestream_on_pd_enabled &&
        isDesktop &&
        !error &&
        data?.getLivestreams?.shows &&
        data?.getLivestreams?.shows.length !== 0
    );

    const tabsWithExpandableTextCtx = useTabsWithExpandableText();
    const { tabsWithExpandableTextState, toggleFullText } =
      tabsWithExpandableTextCtx ?? {};

    const descriptionWithExpandableText =
      tabsWithExpandableTextState?.[TabIds.Description];

    const hasPictograms = Boolean((pictograms?.length ?? 0) > 0);

    const shouldShowFullText =
      !hasPictograms ||
      descriptionWithExpandableText?.shouldShowFullText ||
      !descriptionWithExpandableText?.isTabOpenForTheFirstTime;

    const productCharacteristics = isPerfume
      ? composition.concat(characteristics)
      : characteristics;

    const hasDescription = !isEmpty(description);
    const hasCharacteristics = productCharacteristics.length > 0;

    const hasDetailedInstructions =
      documentation && (documentation.instructions || documentation.document);

    const hasDescriptionContent = Boolean(
      hasDescription ||
        hasDetailedInstructions ||
        documentation?.preparationUsage
    );

    const shouldShowSkinAnalyzer = useIsSkinAnalyzerVisible();

    if (!hasCharacteristics && !hasDescriptionContent) {
      return null;
    }

    const onToggleFullText = () => {
      toggleFullText?.(TabIds.Description);
      trackTab({
        mode: 'open partial',
        timing: getTimeFromInit(),
        trackingLabel: TRACKING_ATTRIBUTES.description,
      });
    };

    return (
      <>
        <Container id="pdDescription">
          <MarginWrapper
            fullWidth={!hasCharacteristics}
            id="pd-description-wrapper"
            data-testid="pd-description-wrapper"
          >
            <AdditionalText />

            {hasPictograms && <Pictograms pictograms={pictograms!} />}

            {hasCharacteristics && (
              <Characteristics characteristics={productCharacteristics} />
            )}

            {shouldShowSkinAnalyzer && (
              <SkinAnalyserWrapper>
                <SkinAnalyser key={product.catalogMasterId} />
              </SkinAnalyserWrapper>
            )}
            <ExpandableText
              onClick={onToggleFullText}
              useOnlyOn="mobile"
              isExpanded={shouldShowFullText}
              buttonText={
                <ExpandButtonText>
                  <FormattedMessage {...messages.showMoreText} />
                </ExpandButtonText>
              }
            >
              {hasDescription && (
                <Description
                  id="pd-description-text"
                  data-testid="pd-description-text"
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              )}

              {hasDescription && descriptionType && (
                <GeneratedTextWrapper>
                  <IconSolidInfo
                    color="icon.disabled"
                    width="1rem"
                    height="1rem"
                  />

                  <FormattedMessage
                    {...messages[
                      descriptionType === 'Composed'
                        ? 'textGeneratedComposed'
                        : 'textGeneratedAi'
                    ]}
                  />
                </GeneratedTextWrapper>
              )}

              {hasDetailedInstructions && (
                <>
                  <SectionTitle>
                    <FormattedMessage {...messages.instructions} />
                  </SectionTitle>
                  <Instructions
                    instructionDocument={documentation.document}
                    instructions={documentation.instructions}
                  />
                </>
              )}

              {(perfumers?.items?.length ?? 0) > 0 && perfumers && (
                <>
                  <SectionTitle>
                    <FormattedMessage {...messages.perfumerSectionTitle} />
                  </SectionTitle>
                  <Perfumers perfumers={perfumers} />
                </>
              )}

              {documentation?.video && (
                <div data-testid="pd-description-video">
                  <SectionTitle>
                    <FormattedMessage {...messages.videoInstructions} />
                  </SectionTitle>
                  <VideoInstructions video={documentation.video} />
                </div>
              )}

              {documentation?.preparationUsage && (
                <>
                  <SectionTitle>
                    <FormattedMessage {...messages.preparationUsage} />
                  </SectionTitle>
                  <Description
                    dangerouslySetInnerHTML={{
                      __html: documentation.preparationUsage,
                    }}
                  />
                </>
              )}

              {isForSensitiveHair && (
                <HairSensitivityTestLink
                  href={formatMessage(messages.sensitiveHairWarningUrl)}
                  rel="nofollow"
                  target="_blank"
                >
                  <FormattedMessage {...messages.sensitiveHairWarningText} />
                </HairSensitivityTestLink>
              )}
            </ExpandableText>
          </MarginWrapper>
        </Container>

        <LivestreamsContainer ref={livestreamsRef}>
          {shouldShowLivestreams && (
            <>
              <SectionTitle>
                <FormattedMessage {...messages.fixedHeaderLivestreams} />
              </SectionTitle>
              <LivestreamBannersCarousel
                livestreams={data?.getLivestreams?.shows as ILivestream[]}
              />
            </>
          )}
        </LivestreamsContainer>

        <VtoLooksContainer>
          {shouldRenderVtoLooks && (
            <>
              <SectionTitle>
                <FormattedMessage {...messages.vtoLooksSectionTitle} />
              </SectionTitle>
              <ErrorBoundary>
                <VtoLooksCarousel />
              </ErrorBoundary>
            </>
          )}
        </VtoLooksContainer>
      </>
    );
  }
);
